import { AlertController } from '@ionic/angular/standalone';
import { AppPreferencesService } from '../../services';
import { CONSTANTS } from '../../constants';
import { Injectable, inject, signal } from '@angular/core';
import { LoginModel } from '../../models';
import { MessageBase } from '../../providers';
import { TranslateService } from '@ngx-translate/core';
import { VaultFacade } from '../../authentication';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';

@Injectable({
  providedIn: 'root',
})
export class BiometricAuthService {
  private readonly alertController = inject(AlertController);
  private readonly appPreferencesService = inject(AppPreferencesService);
  private readonly messageBase = inject(MessageBase);
  private readonly translate = inject(TranslateService);
  private readonly vault = inject(VaultFacade);

  public isBiometricLoginActivated = signal<boolean | null>(null);
  private temporaryLoginData = signal<LoginModel | null>(null);

  public async askIfUserWantsBiometricLogin(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant(_('login_page.label.native_biometric_dialog_title')),
      message: this.translate.instant(_('login_page.label.native_biometric_dialog_description')),
      buttons: [
        {
          text: this.translate.instant(_('login_page.label.decline_biometric_use')),
          role: 'cancel',
          handler: async (): Promise<void> => this.handleBiometricDecision('decline'),
        },
        {
          text: this.translate.instant(_('login_page.label.accept_biometric_use')),
          handler: async (): Promise<void> => this.handleBiometricDecision('accept'),
        },
      ],
    });

    await alert.present();
    await alert.onDidDismiss();
  }

  public async deactivateBiometricLogin(): Promise<void> {
    await this.appPreferencesService.setValue(CONSTANTS.SAVE_BIOMETRIC_KEY, 'false');
    await this.vault.updateUnlockMode('NeverLock');
    await this.updateBiometricStatus();
  }

  public async doBiometricLogin(): Promise<LoginModel | null> {
    try {
      await this.vault.unlockVault();
    } catch {
      // since error is already handled in vault service, we do not need to do anything here
      return null;
    }

    if (!(await this.vault.hasDeviceSecurityEnabled())) {
      await this.messageBase.showError({ message: _('login_page-text.biometric_login_failed_no_biometric') });
      await this.vault.updateUnlockMode('NeverLock');
      return null;
    }

    const loginData = await this.vault.getValue<LoginModel>(CONSTANTS.LOGIN_DATA);

    if (loginData?.password && loginData?.username) {
      return loginData;
    }

    await this.messageBase.showError({ message: _('login_page-text.biometric_login_failed_no_userdata_saved') });
    await this.deactivateBiometricLogin();
    return null;
  }

  public async saveLoginData(loginData: LoginModel): Promise<void> {
    const hasUserAllowedBiometricStorage = await this.loadBiometricLoginSetting();
    if (hasUserAllowedBiometricStorage === true) {
      // User has previously accepted biometric storage - save directly
      await this.vault.setValue<LoginModel>(CONSTANTS.LOGIN_DATA, loginData);
    } else if (hasUserAllowedBiometricStorage === null) {
      // Store temporarily until user makes a decision
      this.temporaryLoginData.set(loginData);
    }
    // User has declined biometric storage - do nothing
  }

  public async handleBiometricDecision(decision: 'accept' | 'decline'): Promise<void> {
    if (decision === 'accept') {
      await this.appPreferencesService.setValue(CONSTANTS.SAVE_BIOMETRIC_KEY, 'true');
      await this.vault.updateUnlockMode('Device');

      // If we have temporary login data, save it to vault
      const tempData = this.temporaryLoginData();
      if (tempData) {
        await this.vault.setValue<LoginModel>(CONSTANTS.LOGIN_DATA, tempData);
        this.temporaryLoginData.set(null);
      }
    } else {
      await this.appPreferencesService.setValue(CONSTANTS.SAVE_BIOMETRIC_KEY, 'false');
      await this.vault.updateUnlockMode('NeverLock');
      this.temporaryLoginData.set(null);
    }
    await this.updateBiometricStatus();
  }

  public async updateBiometricStatus(): Promise<void> {
    const deviceSupportsBiometric = await this.vault.hasDeviceSecurityEnabled();
    const isDeviceSecurityActivated = await this.isDeviceSecurityActivated();
    const hasUserActivatedFeature = await this.appPreferencesService.getValue(CONSTANTS.SAVE_BIOMETRIC_KEY);

    this.isBiometricLoginActivated.set(
      deviceSupportsBiometric && isDeviceSecurityActivated && !!hasUserActivatedFeature,
    );
  }

  public async setBiometricEnabled(enabled: boolean, loginData?: LoginModel): Promise<void> {
    if (enabled) {
      await this.appPreferencesService.setValue(CONSTANTS.SAVE_BIOMETRIC_KEY, 'true');
      if (loginData) {
        await this.saveLoginData(loginData);
      }
      await this.vault.updateUnlockMode('Device');
    } else {
      await this.deactivateBiometricLogin();
    }
    await this.updateBiometricStatus();
  }

  public async hasDeviceSecurityEnabled(): Promise<boolean> {
    return await this.vault.hasDeviceSecurityEnabled();
  }

  public async loadBiometricLoginSetting(): Promise<boolean | null> {
    const result = await this.appPreferencesService.getValue(CONSTANTS.SAVE_BIOMETRIC_KEY);
    return result ? result === 'true' : null;
  }

  private async isDeviceSecurityActivated(): Promise<boolean> {
    const currentUnlockMode = await this.vault.getLastUnlockMode();
    return currentUnlockMode === 'Device';
  }
}
